import React, { useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Badge, Accordion, Card, Table } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import { FaFileInvoice, FaPills, FaSyringe, FaClock, FaFlask, FaXRay, FaCut, FaPaw, FaMoneyBillWave, FaPrint, FaDownload, FaStethoscope, FaMicroscope, FaHeartbeat, FaTrashAlt, FaPlus, FaEdit, FaSave, FaTimes, FaCalendarAlt, FaLock, FaWallet } from "react-icons/fa";
import { MdAccessTime, MdDateRange } from "react-icons/md";
import moment from "moment";

export default function PetInvoiceComponent({ appointmentId, petId, customerId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [invoices, setInvoices] = useState([]);
    const [currentInvoice, setCurrentInvoice] = useState(null);
    const [currentAppointmentId, setCurrentAppointmentId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deletingPaymentId, setDeletingPaymentId] = useState(null);
    const [activeAccordionKey, setActiveAccordionKey] = useState(null);
    const [editingPaymentId, setEditingPaymentId] = useState(null);

    // Customer balance and pending appointments states
    const [customerBalance, setCustomerBalance] = useState(null);
    const [showPendingAppointments, setShowPendingAppointments] = useState(false);
    const [pendingAppointments, setPendingAppointments] = useState([]);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [showCustomerPaymentModal, setShowCustomerPaymentModal] = useState(false);
    const [selectedAppointmentForPayment, setSelectedAppointmentForPayment] = useState(null);
    const [customerPaymentForm, setCustomerPaymentForm] = useState({
        payment_method_id: '',
        amount: 0,
        payment_date: getTodayDate()
    });

    // Multiple payments array for modal
    const [modalPayments, setModalPayments] = useState([
        { id: null, payment_method_id: "", amount: "", payment_date: new Date().toISOString().split('T')[0], isEditing: false }
    ]);

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(false);
    const location = useLocation();

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    const canManagePayments = () => {
        return hasPermission("Appointment Payments Manage");
    };

    const userRole = localStorage.getItem("role") || "";
    const isAdmin = userRole.toLowerCase() === "admin";

    // Get today's date in YYYY-MM-DD format
    function getTodayDate() {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const canModifyInvoice = (isFullyPaid) => {
        if (isFullyPaid) {
            return isAdmin;
        }
        return canManagePayments();
    };

    // Fetch customer balance when customerId is provided
    useEffect(() => {
        if (customerId) {
            fetchCustomerBalance(customerId);
        } else {
            setCustomerBalance(null);
            setPendingAppointments([]);
            setShowPendingAppointments(false);
        }
    }, [customerId]);

    const fetchCustomerBalance = async (custId) => {
        setLoadingBalance(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/appointments/ledger/${custId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            setCustomerBalance(response.data);
        } catch (error) {
            console.error("Error fetching customer balance:", error);
            setCustomerBalance(null);
        } finally {
            setLoadingBalance(false);
        }
    };

    const fetchPendingAppointments = async (custId) => {
        setLoadingBalance(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/appointments/pending/${custId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            if (response.data.status) {
                setPendingAppointments(response.data.appointments || []);
            }
        } catch (error) {
            console.error("Error fetching pending appointments:", error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t('Failed to fetch pending appointments'),
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoadingBalance(false);
        }
    };

    const handleShowPendingAppointments = async () => {
        if (!customerId) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('No customer selected'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (!showPendingAppointments) {
            await fetchPendingAppointments(customerId);
            setShowPendingAppointments(true);
        }
        setShowPendingAppointments(!showPendingAppointments);
    };

    const handleMakeCustomerPayment = (appointment) => {
        setSelectedAppointmentForPayment(appointment);
        setCustomerPaymentForm({
            payment_method_id: '',
            amount: appointment.due,
            payment_date: getTodayDate()
        });
        setShowCustomerPaymentModal(true);
    };

    const handleCustomerPaymentSubmit = async () => {
        if (!customerPaymentForm.payment_method_id) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Please select payment method'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (customerPaymentForm.amount <= 0) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Amount must be greater than 0'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/appointments/allocate-payment`,
                {
                    customer_id: customerId,
                    amount: customerPaymentForm.amount,
                    payment_method_id: customerPaymentForm.payment_method_id,
                    payment_date: customerPaymentForm.payment_date
                },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Payment allocated successfully'),
                    showConfirmButton: false,
                    timer: 2000
                });

                setShowCustomerPaymentModal(false);
                // Refresh balance and pending appointments
                await fetchCustomerBalance(customerId);
                if (showPendingAppointments) {
                    await fetchPendingAppointments(customerId);
                }
                // Refresh invoices to show updated payment status
                await fetchInvoice();

                if (response.data.remaining_unallocated > 0) {
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'info',
                        title: `${t('Remaining amount')}: ${response.data.remaining_unallocated}`,
                        showConfirmButton: false,
                        timer: 3000
                    });
                }
            }
        } catch (error) {
            console.error("Error making payment:", error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: error.response?.data?.message || t('Payment failed'),
                showConfirmButton: false,
                timer: 3000
            });
        }
    };

    useEffect(() => {
        fetchInvoice();
        fetchPaymentMethods();
    }, [appointmentId, petId, location]);

    const fetchInvoice = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-invoice/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data && response.data.success && response.data.invoice) {
                const invoiceData = Array.isArray(response.data.invoice)
                    ? response.data.invoice
                    : [response.data.invoice];
                setInvoices(invoiceData);
            } else if (Array.isArray(response.data?.invoice)) {
                setInvoices(response.data.invoice);
            } else {
                setInvoices([]);
            }
        } catch (error) {
            console.error("Error fetching invoice:", error);
            toast.error(formatLabel(t('failed to load invoice') || 'Failed to load invoice'));
        } finally {
            setLoading(false);
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            setLoadingMethods(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/payment-methods`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                if (Array.isArray(response.data)) {
                    setPaymentMethods(response.data);
                } else if (response.data.paymentMethods && Array.isArray(response.data.paymentMethods)) {
                    setPaymentMethods(response.data.paymentMethods);
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    setPaymentMethods(response.data.data);
                } else {
                    setPaymentMethods([]);
                }
            }
        } catch (error) {
            console.error("Error fetching payment methods:", error);
            setPaymentMethods([]);
        } finally {
            setLoadingMethods(false);
        }
    };

    const getTypeIcon = (type) => {
        const typeLower = type?.toLowerCase() || '';
        if (typeLower.includes('consultancy')) return <FaStethoscope className="text-info" />;
        if (typeLower.includes('vaccine')) return <FaSyringe className="text-success" />;
        if (typeLower.includes('lab')) return <FaFlask className="text-info" />;
        if (typeLower.includes('radiology')) return <FaXRay className="text-primary" />;
        if (typeLower.includes('surgery')) return <FaCut className="text-danger" />;
        if (typeLower.includes('deworming')) return <FaPaw className="text-warning" />;
        if (typeLower.includes('prescription')) return <FaPills className="text-secondary" />;
        if (typeLower.includes('grooming')) return <FaCut className="text-info" />;
        return <FaHeartbeat className="text-muted" />;
    };

    const getTypeBadgeColor = (type) => {
        const typeLower = type?.toLowerCase() || '';
        if (typeLower.includes('consultancy')) return 'info';
        if (typeLower.includes('vaccine')) return 'success';
        if (typeLower.includes('lab')) return 'info';
        if (typeLower.includes('radiology')) return 'primary';
        if (typeLower.includes('surgery')) return 'danger';
        if (typeLower.includes('deworming')) return 'warning';
        if (typeLower.includes('prescription')) return 'secondary';
        if (typeLower.includes('grooming')) return 'info';
        return 'dark';
    };

    const calculateGrandTotal = (invoice) => {
        if (!invoice || !invoice.detail) return 0;
        let total = 0;
        invoice.detail.forEach(item => {
            if (item.formatted_detail && !Array.isArray(item.formatted_detail) && item.formatted_detail.price != null) {
                total += parseFloat(item.formatted_detail.price) || 0;
            }
            else if (item.model && item.model.price != null) {
                total += parseFloat(item.model.price) || 0;
            }
            else if (Array.isArray(item.formatted_detail)) {
                item.formatted_detail.forEach(detail => {
                    if (Array.isArray(detail.price)) {
                        detail.price.forEach(price => {
                            total += parseFloat(price) || 0;
                        });
                    } else if (detail.price != null) {
                        total += parseFloat(detail.price) || 0;
                    }
                });
            }
        });
        return total;
    };

    const calculatePaidTotal = (invoice) => {
        if (!invoice || !invoice.payment) return 0;
        let paid = 0;
        invoice.payment.forEach(payment => {
            if (payment.amount) {
                paid += parseFloat(payment.amount) || 0;
            }
        });
        return paid;
    };

    const getRemainingAmount = (invoice) => {
        return calculateGrandTotal(invoice) - calculatePaidTotal(invoice);
    };

    const getParsedAmount = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        const number = typeof value === 'string' ? parseFloat(value) : Number(value);
        return Number.isNaN(number) ? 0 : number;
    };

    // Calculate total of new payments in modal
    const getModalPaymentsTotal = () => {
        return modalPayments.reduce((sum, p) => {
            if (p.amount && !p.isEditing) {
                return sum + getParsedAmount(p.amount);
            }
            return sum;
        }, 0);
    };

    const getModalRemainingAfterPayments = () => {
        if (!currentInvoice) return 0;
        const remaining = getRemainingAmount(currentInvoice);
        const currentPaymentsTotal = getModalPaymentsTotal();
        return remaining - currentPaymentsTotal;
    };

    // Open Add Payment Modal
    const handleOpenAddPayment = (invoice) => {
        const remaining = getRemainingAmount(invoice);
        const isFullyPaid = remaining <= 0;

        if (!canModifyInvoice(isFullyPaid)) {
            if (isFullyPaid) {
                toast.error(t('invoice_is_locked_fully_paid') || 'Invoice is locked as it is fully paid. Only admin can modify.');
            } else {
                toast.error(t('you_dont_have_permission'));
            }
            return;
        }

        setCurrentInvoice(invoice);
        setCurrentAppointmentId(appointmentId);
        setEditingPaymentId(null);
        setModalPayments([
            { id: null, payment_method_id: "", amount: remaining > 0 ? (Number.isInteger(remaining) ? String(remaining) : remaining) : "", payment_date: new Date().toISOString().split('T')[0], isEditing: false }
        ]);
        setShowPaymentModal(true);
    };

    // Open Edit Payment Modal (Edit existing payment)
    const handleOpenEditPayment = (payment, invoice) => {
        const remaining = getRemainingAmount(invoice);
        const isFullyPaid = remaining <= 0;

        if (!canModifyInvoice(isFullyPaid)) {
            if (isFullyPaid) {
                toast.error(t('invoice_is_locked_fully_paid') || 'Invoice is locked as it is fully paid. Only admin can modify.');
            } else {
                toast.error(t('you_dont_have_permission'));
            }
            return;
        }

        setCurrentInvoice(invoice);
        setCurrentAppointmentId(appointmentId);
        setEditingPaymentId(payment.id);
        setModalPayments([
            {
                id: payment.id,
                payment_method_id: payment.payment_method_id,
                amount: payment.amount,
                payment_date: payment.payment_date ? moment(payment.payment_date).format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
                isEditing: true
            }
        ]);
        setShowPaymentModal(true);
    };

    // Add new payment row in modal
    const handleAddPaymentRow = () => {
        const remaining = getModalRemainingAfterPayments();
        if (remaining <= 0) {
            toast.warning(t('no_remaining_amount_to_add') || 'No remaining amount to add more payments');
            return;
        }

        setModalPayments([...modalPayments, {
            id: null,
            payment_method_id: "",
            amount: remaining > 0 ? (Number.isInteger(remaining) ? String(remaining) : remaining) : "",
            payment_date: new Date().toISOString().split('T')[0],
            isEditing: false
        }]);
    };

    // Remove payment row from modal
    const handleRemovePaymentRow = (index) => {
        if (modalPayments.length === 1) {
            toast.warning(t('at_least_one_payment_required') || 'At least one payment is required');
            return;
        }
        const newPayments = modalPayments.filter((_, i) => i !== index);
        setModalPayments(newPayments);
    };

    // Update payment row data
    const handlePaymentRowChange = (index, field, value) => {
        const newPayments = [...modalPayments];
        newPayments[index][field] = value;

        // Validate amount
        if (field === 'amount') {
            const remaining = getModalRemainingAfterPayments();
            const currentOtherPayments = newPayments.reduce((sum, p, i) => {
                if (i !== index && p.amount && !p.isEditing) {
                    return sum + getParsedAmount(p.amount);
                }
                return sum;
            }, 0);
            const maxAllowed = remaining + currentOtherPayments;
            if (getParsedAmount(value) > maxAllowed) {
                toast.warning(`${t('Amount cannot exceed remaining balance')}: ${formatAmount(maxAllowed)}`);
                newPayments[index][field] = maxAllowed.toString();
            }
        }

        setModalPayments(newPayments);
    };

    // Save all payments
    const handleSavePayments = async () => {
        // Filter valid payments
        const validPayments = modalPayments.filter(p => p.payment_method_id && p.amount && getParsedAmount(p.amount) > 0);



        // Check total amount
        const totalNewAmount = validPayments.reduce((sum, p) => sum + getParsedAmount(p.amount), 0);
        const currentPaidTotal = calculatePaidTotal(currentInvoice);
        const grandTotal = calculateGrandTotal(currentInvoice);

        let finalTotal = currentPaidTotal;
        validPayments.forEach(p => {
            if (p.isEditing && p.id) {
                const originalPayment = currentInvoice?.payment?.find(pay => pay.id === p.id);
                finalTotal = finalTotal - (originalPayment ? getParsedAmount(originalPayment.amount) : 0) + getParsedAmount(p.amount);
            } else {
                finalTotal = finalTotal + getParsedAmount(p.amount);
            }
        });

        if (finalTotal > grandTotal) {
            toast.error(t('total_payment_amount_exceeds_grand_total'));
            return;
        }

        const currentAccordionKey = activeAccordionKey;
        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const paymentsArray = validPayments.map(p => {
                if (p.isEditing && p.id) {
                    return {
                        id: p.id,
                        payment_method_id: parseInt(p.payment_method_id),
                        amount: getParsedAmount(p.amount),
                        payment_date: p.payment_date
                    };
                } else {
                    return {
                        payment_method_id: parseInt(p.payment_method_id),
                        amount: getParsedAmount(p.amount),
                        payment_date: p.payment_date
                    };
                }
            });

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-invoice-add-payment/${currentInvoice.id}`,
                { payments: paymentsArray },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                const msg = `${t('payments_processed_successfully') || 'Payments processed successfully'}${response.data.message ? ' - ' + response.data.message : ''}`;

                toast.success(msg);
                setShowPaymentModal(false);
                setEditingPaymentId(null);
                setModalPayments([
                    { id: null, payment_method_id: "", amount: "", payment_date: new Date().toISOString().split('T')[0], isEditing: false }
                ]);
                await fetchInvoice();
                // Refresh customer balance after payment
                if (customerId) {
                    await fetchCustomerBalance(customerId);
                    if (showPendingAppointments) {
                        await fetchPendingAppointments(customerId);
                    }
                }
                setActiveAccordionKey(currentAccordionKey);
            } else {
                toast.error(response.data.message || t('payment_failed'));
            }
        } catch (error) {
            console.error("Error saving payments:", error);
            toast.error(error.response?.data?.message || t('payment_failed'));
        } finally {
            setSubmitting(false);
        }
    };

    // Delete single payment from existing payments
    const handleDeletePayment = async (paymentId, invoice) => {
        const remaining = getRemainingAmount(invoice);
        const isFullyPaid = remaining <= 0;

        if (!canModifyInvoice(isFullyPaid)) {
            if (isFullyPaid) {
                toast.error(t('invoice_is_locked_fully_paid') || 'Invoice is locked as it is fully paid. Only admin can delete payments.');
            } else {
                toast.error(t('you_dont_have_permission'));
            }
            return;
        }

        const result = await Swal.fire({
            title: t('confirm_delete'),
            text: t('are_you_sure_delete_payment'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel'),
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        const currentAccordionKey = activeAccordionKey;
        setDeletingPaymentId(paymentId);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/payment-destroy/${paymentId}`,
                {},
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.message === 'Payment deleted successfully') {
                toast.success(t('payment_deleted_successfully'));
                await fetchInvoice();
                // Refresh customer balance after payment deletion
                if (customerId) {
                    await fetchCustomerBalance(customerId);
                    if (showPendingAppointments) {
                        await fetchPendingAppointments(customerId);
                    }
                }
                setActiveAccordionKey(currentAccordionKey);
            } else {
                toast.error(response.data.message || t('failed_to_delete_payment'));
            }
        } catch (error) {
            console.error("Error deleting payment:", error);
            toast.error(error.response?.data?.message || t('failed_to_delete_payment'));
        } finally {
            setDeletingPaymentId(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'N/A';
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        return timeString;
    };

    const formatLabel = (text) => {
        if (text === null || text === undefined) return '';
        const cleaned = String(text).replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
        return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const formatAmount = (value) => {
        if (value === null || value === undefined || value === '') return '0';
        const number = typeof value === 'string' ? parseFloat(value) : Number(value);
        if (Number.isNaN(number)) return String(value);
        return Number.isInteger(number) ? number.toString() : number;
    };

    const handlePrint = (invoiceId) => {
        if (!invoiceId) return;
        const printWindow = window.open(`${process.env.REACT_APP_URL}/invoice-print/${invoiceId}`, '_blank');
        if (printWindow) printWindow.focus();
    };

    const getFlatItems = (invoice) => {
        const items = [];
        if (!invoice || !invoice.detail) return items;

        let serialCounter = 1;

        invoice.detail.forEach((item) => {
            if (!item.formatted_detail) return;

            if (item.formatted_detail && !Array.isArray(item.formatted_detail)) {
                const detailData = item.formatted_detail;
                items.push({
                    serialNo: serialCounter++,
                    id: detailData.id,
                    type: detailData.type || detailData.name || 'Service',
                    name: detailData.name || 'N/A',
                    quantity: 1,
                    total: parseFloat(detailData.price) || 0,
                    date: detailData.date || invoice.created_at
                });
            }
            else if (Array.isArray(item.formatted_detail)) {
                item.formatted_detail.forEach((detail) => {
                    if (detail.product && Array.isArray(detail.product)) {
                        detail.product.forEach((product, productIndex) => {
                            const price = detail.price && detail.price[productIndex] ? parseFloat(detail.price[productIndex]) : 0;
                            items.push({
                                serialNo: serialCounter++,
                                id: detail.id,
                                type: detail.type || 'In House Prescription',
                                name: product.name || 'Product',
                                quantity: product.quantity || 1,
                                total: price,
                                date: detail.date || invoice.created_at
                            });
                        });
                    }
                    else if (detail.name) {
                        items.push({
                            serialNo: serialCounter++,
                            id: detail.id,
                            type: detail.type || 'Service',
                            name: detail.name,
                            quantity: 1,
                            total: parseFloat(detail.price) || 0,
                            date: detail.date || invoice.created_at
                        });
                    }
                });
            }
            else if (item.model) {
                const detailData = item.model;
                items.push({
                    serialNo: serialCounter++,
                    id: detailData.id,
                    type: detailData.type || detailData.name || 'Service',
                    name: detailData.name || 'N/A',
                    quantity: 1,
                    total: parseFloat(detailData.price) || 0,
                    date: detailData.date || invoice.created_at
                });
            }
        });

        return items;
    };

    const invoicesToShow = invoices.filter(invoice => getFlatItems(invoice).length > 0);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading invoice')}</p>
            </div>
        );
    }

    if (!invoicesToShow || invoicesToShow.length === 0) {
        return (
            <Alert variant="warning" className="text-center py-4">
                <FaFileInvoice size={40} className="mb-2 text-muted" />
                <p className="mb-0">{t('no invoice found')}</p>
            </Alert>
        );
    }

    return (
        <div className="pet-invoice-component">
            {/* Customer Balance Display - Professional Card */}
            {customerBalance && customerBalance.total_balance > 0 && (
                <div className="mb-4" style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    boxShadow: '0 8px 32px rgba(15, 52, 96, 0.35)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background decorative circles */}
                    <div style={{
                        position: 'absolute', top: '-40px', right: '-40px',
                        width: '150px', height: '150px', borderRadius: '50%',
                        background: 'rgba(255,255,255,0.04)', pointerEvents: 'none'
                    }} />
                    <div style={{
                        position: 'absolute', bottom: '-20px', left: '30%',
                        width: '100px', height: '100px', borderRadius: '50%',
                        background: 'rgba(255,255,255,0.03)', pointerEvents: 'none'
                    }} />

                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        {/* Left: Balance info */}
                        <div className="d-flex align-items-center gap-3">
                            <div style={{
                                background: 'rgba(255,193,7,0.15)',
                                border: '1px solid rgba(255,193,7,0.3)',
                                borderRadius: '12px',
                                width: '52px', height: '52px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <FaWallet size={22} color="#ffc107" />
                            </div>
                            <div>
                                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 500, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '2px' }}>
                                    {t('Customer Balance')}
                                </div>
                                <div style={{ color: '#ffffff', fontSize: '26px', fontWeight: 700, lineHeight: 1.1 }}>
                                    {parseFloat(customerBalance.total_balance).toLocaleString()}
                                </div>
                            </div>
                        </div>

                        {/* Middle: Stats chips */}
                        <div className="d-flex gap-2 flex-wrap">
                            <div style={{
                                background: 'rgba(255,255,255,0.08)',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: '10px', padding: '8px 16px', textAlign: 'center'
                            }}>

                            </div>
                            {customerBalance.total_due != null && (
                                <div style={{
                                    background: 'rgba(255,255,255,0.08)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    borderRadius: '10px', padding: '8px 16px', textAlign: 'center'
                                }}>
                                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginBottom: '2px' }}>{t('Total Due')}</div>
                                    <div style={{ color: '#ffc107', fontWeight: 700, fontSize: '18px' }}>
                                        {parseFloat(customerBalance.total_due).toLocaleString()}
                                    </div>
                                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>{t('outstanding')}</div>
                                </div>
                            )}
                        </div>

                        {/* Right: Action button */}
                        <Button
                            onClick={handleShowPendingAppointments}
                            style={{
                                background: 'linear-gradient(135deg, #ffc107, #ff9800)',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '10px 20px',
                                fontWeight: 600,
                                fontSize: '13px',
                                color: '#1a1a2e',
                                boxShadow: '0 4px 15px rgba(255,193,7,0.3)',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FaFileInvoice className="me-2" size={14} />
                            {t('View Pending Appointments')}
                        </Button>
                    </div>
                </div>
            )}


            <Accordion
                activeKey={activeAccordionKey}
                onSelect={(key) => setActiveAccordionKey(key)}
                className="mb-4"
            >
                {invoicesToShow.map((invoice, invoiceIndex) => {
                    const grandTotal = calculateGrandTotal(invoice);
                    const paidTotal = calculatePaidTotal(invoice);
                    const remainingAmount = getRemainingAmount(invoice);
                    const isFullyPaid = remainingAmount <= 0;
                    const flatItems = getFlatItems(invoice).filter(item => item.total > 0);
                    const invoiceKey = invoiceIndex.toString();
                    const canModify = canModifyInvoice(isFullyPaid);

                    return (
                        <Accordion.Item eventKey={invoiceKey} key={invoiceIndex}>
                            <Accordion.Header>
                                <div className="w-100 d-flex justify-content-between align-items-center pe-3">
                                    <div className="d-flex align-items-center gap-3">
                                        <strong className="me-3">{invoice.appointment_number}</strong>
                                        <small className="text-muted">{formatDate(invoice.appointment_date)} {formatTime(invoice.appointment_time_slot)}</small>
                                        {isFullyPaid ? (
                                            <Badge bg="success" className="d-flex align-items-center gap-1">
                                                <FaLock size={10} /> {t('Paid')}
                                            </Badge>
                                        ) : (
                                            <Badge bg="warning" className="d-flex align-items-center gap-1">
                                                {t('Pending')}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="d-flex gap-2 align-items-center">
                                        {!isFullyPaid && canManagePayments() && (
                                            <Button
                                                variant="success"
                                                size="sm"
                                                className="d-flex align-items-center gap-2"
                                                onClick={() => handleOpenAddPayment(invoice)}
                                            >
                                                <FaMoneyBillWave /> {t('make payment')}
                                            </Button>
                                        )}

                                        <Button
                                            variant="success"
                                            size="sm"
                                            className="d-flex align-items-center gap-2"
                                            onClick={() => handlePrint(invoice.id)}
                                        >
                                            <FaPrint /> {t('Print')}
                                        </Button>

                                        <strong className="ms-3">{formatAmount(grandTotal)}</strong>
                                    </div>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="p-0">
                                <div className="bg-light p-3 p-md-4">
                                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                                        <Card.Body className="p-0">
                                            {/* Invoice Table Section */}
                                            <div className="p-4 p-md-5 bg-white">
                                                <div className="d-flex justify-content-between align-items-center mb-4">
                                                    <h5 className="fw-bold mb-0 text-dark">{t('invoice_details')}</h5>
                                                    {isFullyPaid && !isAdmin && (
                                                        <Badge bg="secondary" className="px-3 py-2 rounded-pill">
                                                            <FaLock className="me-1" /> {t('Locked')}
                                                        </Badge>
                                                    )}
                                                    {isFullyPaid && isAdmin && (
                                                        <Badge bg="info" className="px-3 py-2 rounded-pill">
                                                            <FaEdit className="me-1" /> {t('Admin - Editable')}
                                                        </Badge>
                                                    )}
                                                </div>

                                                {flatItems.length > 0 ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-hover align-middle">
                                                            <thead className="bg-light text-secondary">
                                                                <tr>
                                                                    <th className="ps-4" style={{ width: '5%' }}>#</th>
                                                                    <th style={{ width: '20%' }}>{t('type')}</th>
                                                                    <th style={{ width: '40%' }}>{t('name')}</th>
                                                                    <th className="text-center" style={{ width: '20%' }}>{t('date')}</th>
                                                                    <th className="text-end pe-4" style={{ width: '15%' }}>{t('total')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {flatItems.map((item, index) => (
                                                                    <tr key={item.serialNo} className={(isFullyPaid && !isAdmin) ? "opacity-75" : ""}>
                                                                        <td className="ps-4 text-muted">{index + 1}</td>
                                                                        <td>
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <span className="text-primary">{getTypeIcon(item.type)}</span>
                                                                                <Badge bg={getTypeBadgeColor(item.type)} className="fw-medium">
                                                                                    {formatLabel(item.type)}
                                                                                </Badge>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="fw-semibold text-dark">
                                                                                {formatLabel(item.name)}
                                                                                {item.quantity > 1 && (
                                                                                    <span className="badge bg-light text-dark ms-2 border">x{item.quantity}</span>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <div className="text-muted small">
                                                                                <FaCalendarAlt className="me-1" size={11} />
                                                                                {moment(item.date).format('DD MMM hh:mm A')}
                                                                            </div>
                                                                        </td>
                                                                        <td className="text-end pe-4 fw-bold text-dark">{formatAmount(item.total)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <Alert variant="info" className="text-center py-4">
                                                        {t('no_items_found_in_invoice')}
                                                    </Alert>
                                                )}
                                            </div>

                                            {/* Payment History and Summary - Side by Side */}
                                            <div className="p-4 p-md-5 border-top bg-light">
                                                <Row className="g-4">
                                                    {/* Payment History Column */}
                                                    <Col lg={7}>
                                                        <div className="bg-white p-4 rounded-4 shadow-sm border h-100">
                                                            <div className="d-flex justify-content-between align-items-center mb-4">
                                                                <h6 className="fw-bold mb-0 text-uppercase text-secondary small">
                                                                    {t('payment history')}
                                                                </h6>
                                                                {!isFullyPaid && canManagePayments() && (
                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        onClick={() => handleOpenAddPayment(invoice)}
                                                                    >
                                                                        <FaPlus className="me-1" size={12} /> {t('add payment')}
                                                                    </Button>
                                                                )}
                                                            </div>

                                                            {invoice.payment && invoice.payment.length > 0 ? (
                                                                <div className="table-responsive">
                                                                    <Table className="table align-middle">
                                                                        <thead className="table-light">
                                                                            <tr>
                                                                                <th>{t('Date')}</th>
                                                                                <th>{t('Method')}</th>
                                                                                <th className="text-end">{t('Amount')}</th>
                                                                                {canModify && <th className="text-center" style={{ width: '100px' }}>{t('Actions')}</th>}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {invoice.payment.map((payment, idx) => (
                                                                                <tr key={idx}>
                                                                                    <td>
                                                                                        <FaCalendarAlt className="me-2 text-muted" size={12} />
                                                                                        {formatDate(payment.payment_date)}
                                                                                    </td>
                                                                                    <td>
                                                                                        <Badge bg="light" text="dark" className="border">
                                                                                            {payment.method?.name || 'N/A'}
                                                                                        </Badge>
                                                                                    </td>
                                                                                    <td className="text-end fw-bold text-success">
                                                                                        {formatAmount(payment.amount)}
                                                                                    </td>
                                                                                    {canModify && (
                                                                                        <td className="text-center">
                                                                                            <div className="d-flex gap-2 justify-content-center">
                                                                                                <Button
                                                                                                    variant="link"
                                                                                                    size="sm"
                                                                                                    className="p-0 text-primary"
                                                                                                    onClick={() => handleOpenEditPayment(payment, invoice)}
                                                                                                    title={t('edit payment')}
                                                                                                >
                                                                                                    <FaEdit size={16} />
                                                                                                </Button>
                                                                                                <Button
                                                                                                    variant="link"
                                                                                                    size="sm"
                                                                                                    className="p-0 text-danger"
                                                                                                    onClick={() => handleDeletePayment(payment.id, invoice)}
                                                                                                    disabled={deletingPaymentId === payment.id}
                                                                                                    title={t('delete payment')}
                                                                                                >
                                                                                                    {deletingPaymentId === payment.id ? (
                                                                                                        <Spinner animation="border" size="sm" />
                                                                                                    ) : (
                                                                                                        <FaTrashAlt size={16} />
                                                                                                    )}
                                                                                                </Button>
                                                                                            </div>
                                                                                        </td>
                                                                                    )}
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                        <tfoot className="table-active">
                                                                            <tr>
                                                                                <td colSpan="2" className="fw-bold">{t('Total Paid')}:</td>
                                                                                <td className="text-end fw-bold text-success">{formatAmount(paidTotal)}</td>
                                                                                {canModify && <td></td>}
                                                                            </tr>
                                                                        </tfoot>
                                                                    </Table>
                                                                </div>
                                                            ) : (
                                                                <div className="text-center text-muted py-4">
                                                                    {t('no payments recorded')}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Col>

                                                    {/* Summary Column */}
                                                    <Col lg={5}>
                                                        <div className="bg-white p-4 rounded-4 shadow-sm border h-100">
                                                            <h6 className="fw-bold mb-3 text-uppercase text-secondary small">{t('Summary')}</h6>
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <span className="text-muted">{t('sub total')}</span>
                                                                <span className="fw-semibold">{formatAmount(grandTotal)}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <span className="text-muted">{t('total paid')}</span>
                                                                <span className="fw-bold text-success">{formatAmount(paidTotal)}</span>
                                                            </div>
                                                            <hr />
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <span className="h6 mb-0 fw-bold">{t('pending amount')}</span>
                                                                <span className="h6 mb-0 fw-bold text-danger">{formatAmount(remainingAmount)}</span>
                                                            </div>
                                                            <div className="mt-3">
                                                                {isFullyPaid ? (
                                                                    <div className="p-2 rounded-3 text-center fw-bold small text-uppercase bg-success-subtle text-success d-flex align-items-center justify-content-center gap-2">
                                                                        <FaLock size={12} /> {t('Fully Paid')}
                                                                    </div>
                                                                ) : (
                                                                    <div className="p-2 rounded-3 text-center fw-bold small text-uppercase bg-warning-subtle text-warning">
                                                                        {t('Pending Payment')}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>

                                            {isFullyPaid && !isAdmin && (
                                                <div className="bg-secondary text-white p-3 text-center small fw-medium">
                                                    <FaLock className="me-2" /> {t('This invoice is locked as it is fully paid. Only admin can modify.')}
                                                </div>
                                            )}
                                            {isFullyPaid && isAdmin && (
                                                <div className="bg-info text-white p-3 text-center small fw-medium">
                                                    <FaEdit className="me-2" /> {t('Admin: Fully paid invoice - Edit access granted')}
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    );
                })}
            </Accordion>

            {/* Payment Modal with Multiple Payments Support */}
            <Modal show={showPaymentModal} onHide={() => {
                setShowPaymentModal(false);
                setEditingPaymentId(null);
                setModalPayments([
                    { id: null, payment_method_id: "", amount: "", payment_date: new Date().toISOString().split('T')[0], isEditing: false }
                ]);
            }} size="lg" centered>
                <Modal.Header closeButton className="bg-light p-2">
                    <Modal.Title className="fs-6">
                        <FaMoneyBillWave className="me-2 text-success" />
                        {editingPaymentId ? t('Edit Payments') : t('Add Payments')}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-3">
                    {/* Balance Info */}
                    <Alert variant="info" className="mb-4 p-2">
                        <Row className="g-2">
                            <Col xs={6}>
                                <small className="text-muted d-block">{t('total amount')}</small>
                                <strong className="fs-6">{formatAmount(calculateGrandTotal(currentInvoice))}</strong>
                            </Col>
                            <Col xs={6}>
                                <small className="text-muted d-block">{t('remaining balance')}</small>
                                <strong className="fs-6 text-danger">{formatAmount(getRemainingAmount(currentInvoice))}</strong>
                            </Col>
                        </Row>
                        <hr className="my-2" />
                        <Row className="g-2">
                            <Col xs={6}>
                                <small className="text-muted d-block">{t('total paid')}</small>
                                <strong className="fs-6 text-success">{formatAmount(calculatePaidTotal(currentInvoice))}</strong>
                            </Col>
                            <Col xs={6}>
                                <small className="text-muted d-block">{t('new payments total')}</small>
                                <strong className="fs-6 text-primary">{formatAmount(getModalPaymentsTotal())}</strong>
                            </Col>
                        </Row>
                        <hr className="my-2" />
                        <Row className="g-2">
                            <Col xs={12}>
                                <small className="text-muted d-block">{t('remaining after new payments')}</small>
                                <strong className="fs-6 text-danger">{formatAmount(getModalRemainingAfterPayments())}</strong>
                            </Col>
                        </Row>
                    </Alert>

                    {/* Existing Payments List (for edit mode) */}
                    {editingPaymentId && currentInvoice?.payment && currentInvoice.payment.length > 0 && (
                        <div className="mb-4">
                            <h6 className="small fw-bold mb-2">{t('existing payments')}</h6>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t('Date')}</th>
                                            <th>{t('Method')}</th>
                                            <th className="text-end">{t('Amount')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentInvoice.payment.map((payment, idx) => (
                                            <tr key={idx}>
                                                <td>{formatDate(payment.payment_date)}</td>
                                                <td>{payment.method?.name || 'N/A'}</td>
                                                <td className="text-end">{formatAmount(payment.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Multiple Payments Rows */}
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="small fw-bold mb-0">{t('payment details')}</h6>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleAddPaymentRow}
                                className="d-flex align-items-center gap-1"
                            >
                                <FaPlus size={10} /> {t('add more payment')}
                            </Button>
                        </div>

                        {modalPayments.map((payment, index) => {
                            // Get available payment methods (exclude already selected ones in other rows)
                            const selectedMethodIds = modalPayments
                                .filter((_, i) => i !== index && payment.payment_method_id)
                                .map(p => p.payment_method_id)
                                .filter(id => id);
                            const availableMethods = paymentMethods.filter(method =>
                                !selectedMethodIds.includes(method.id) || method.id === payment.payment_method_id
                            );

                            return (
                                <div
                                    key={index}
                                    className="border rounded p-2 mb-2"
                                    style={{
                                        backgroundColor: payment.isEditing ? '#fff3cd' : '#ffffff',
                                        borderLeft: payment.isEditing ? '3px solid #ffc107' : '1px solid #dee2e6'
                                    }}
                                >
                                    <Row className="g-2 align-items-end">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold mb-0">{t('payment method')}</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={payment.payment_method_id}
                                                    onChange={(e) => handlePaymentRowChange(index, 'payment_method_id', e.target.value)}
                                                    disabled={loadingMethods}
                                                >
                                                    <option value="">{t('select')}</option>
                                                    {availableMethods.map(method => (
                                                        <option key={method.id} value={method.id}>
                                                            {method.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold mb-0">{t('amount')}</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="number"
                                                    step="0.01"
                                                    min="0.01"
                                                    value={payment.amount}
                                                    onChange={(e) => handlePaymentRowChange(index, 'amount', e.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold mb-0">{t('payment date')}</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="date"
                                                    value={payment.payment_date}
                                                    onChange={(e) => handlePaymentRowChange(index, 'payment_date', e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={2} className="text-end">
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleRemovePaymentRow(index)}
                                                disabled={modalPayments.length === 1}
                                                title={t('remove')}
                                            >
                                                <FaTrashAlt size={12} />
                                            </Button>
                                        </Col>
                                    </Row>
                                    {payment.isEditing && (
                                        <small className="text-muted d-block mt-1">
                                            <FaEdit size={10} className="me-1" /> {t('editing existing payment')}
                                        </small>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Modal.Body>

                <Modal.Footer className="bg-light p-2">
                    <Button size="sm" variant="secondary" onClick={() => {
                        setShowPaymentModal(false);
                        setEditingPaymentId(null);
                        setModalPayments([
                            { id: null, payment_method_id: "", amount: "", payment_date: new Date().toISOString().split('T')[0], isEditing: false }
                        ]);
                    }}>
                        {t('cancel')}
                    </Button>
                    <Button
                        size="sm"
                        variant="success"
                        onClick={handleSavePayments}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {t('processing')}
                            </>
                        ) : (
                            <>
                                <FaSave className="me-2" size={12} />
                                {t('save payments')}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Customer Payment Modal for Pending Appointments */}
            <Modal show={showCustomerPaymentModal} onHide={() => setShowCustomerPaymentModal(false)} size="md">
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>{t('Make Payment')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAppointmentForPayment && (
                        <>
                            <div className="mb-3">
                                <strong>{t('Appointment #')}:</strong> {selectedAppointmentForPayment.appointment_number}<br />
                                <strong>{t('Due Amount')}:</strong> <span className="text-danger fw-bold">{parseFloat(selectedAppointmentForPayment.due)}</span>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Payment Method')} <span className="text-danger">*</span></Form.Label>
                                <select
                                    className="form-control"
                                    value={customerPaymentForm.payment_method_id}
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, payment_method_id: e.target.value })}
                                >
                                    <option value="">{t('Select Payment Method')}</option>
                                    {paymentMethods.map(pm => (
                                        <option key={pm.id} value={pm.id}>{pm.name}</option>
                                    ))}
                                </select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Amount')} <span className="text-danger">*</span></Form.Label>
                                <input
                                    type="number"
                                    className="form-control"
                                    step="0.01"
                                    min="0.01"
                                    max={selectedAppointmentForPayment.due}
                                    value={customerPaymentForm.amount}
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, amount: parseFloat(e.target.value) || 0 })}
                                />
                                <small className="text-muted">{t('Max amount')}: {parseFloat(selectedAppointmentForPayment.due)}</small>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Payment Date')} <span className="text-danger">*</span></Form.Label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={customerPaymentForm.payment_date}
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, payment_date: e.target.value })}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="p-3">
                    <Button variant="secondary" onClick={() => setShowCustomerPaymentModal(false)}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleCustomerPaymentSubmit}>
                        {t('Pay Now')}
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Pending Appointments Modal */}
            <Modal
                show={showPendingAppointments}
                onHide={() => setShowPendingAppointments(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton style={{
                    background: 'linear-gradient(135deg, #1a1a2e, #0f3460)',
                    borderBottom: 'none',
                    padding: '20px 24px'
                }}>
                    <Modal.Title style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>
                        <FaFileInvoice className="me-2" style={{ color: '#ffc107' }} />
                        {t('Pending Appointments')}
                        {pendingAppointments.length > 0 && (
                            <Badge style={{
                                background: 'rgba(255,193,7,0.2)',
                                color: '#ffc107',
                                border: '1px solid rgba(255,193,7,0.3)',
                                marginLeft: '10px',
                                fontWeight: 600,
                                fontSize: '12px',
                                padding: '4px 10px',
                                borderRadius: '20px'
                            }}>
                                {pendingAppointments.length} {t('pending')}
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: '0', background: '#f8f9fa' }}>
                    {loadingBalance ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">{t('Loading...')}</p>
                        </div>
                    ) : pendingAppointments.length === 0 ? (
                        <div className="text-center py-5">
                            <FaFileInvoice size={40} className="text-muted mb-3" />
                            <p className="text-muted">{t('No pending appointments found')}</p>
                        </div>
                    ) : (
                        <div style={{ padding: '20px' }}>
                            {/* Summary bar */}
                            <div style={{
                                borderRadius: '12px',
                                padding: '16px 20px',
                                marginBottom: '20px',
                                display: 'flex',
                                gap: '30px',
                                flexWrap: 'wrap'
                            }}>
                                <div>
                                    <div style={{ color: 'black', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{t('Total Due')}</div>
                                    <div style={{ color: '#ff6b6b', fontWeight: 700, fontSize: '20px' }}>
                                        {pendingAppointments.reduce((s, a) => s + parseFloat(a.due || 0), 0).toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: 'black', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{t('Total Paid')}</div>
                                    <div style={{ color: '#4ade80', fontWeight: 700, fontSize: '20px' }}>
                                        {pendingAppointments.reduce((s, a) => s + parseFloat(a.paid || 0), 0).toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: 'black', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{t('Appointments')}</div>
                                    <div style={{ color: '#ffc107', fontWeight: 700, fontSize: '20px' }}>{pendingAppointments.length}</div>
                                </div>
                            </div>

                            {/* Table */}
                            <div >
                                <table className="table mb-0">
                                    <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                            <th >{t('Appointment #')}</th>
                                            <th >{t('Date')}</th>
                                            <th >{t('Total')}</th>
                                            <th >{t('Paid')}</th>
                                            <th>{t('Due')}</th>
                                            <th >{t('Status')}</th>
                                            <th>{t('Action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingAppointments.map((appointment, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '14px 16px', fontWeight: 600, color: '#1e293b', border: 'none' }}>
                                                    <FaCalendarAlt className="me-2" size={12} style={{ color: '#94a3b8' }} />
                                                    {appointment.appointment_number}
                                                </td>
                                                <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '13px', border: 'none' }}>
                                                    {moment(appointment.appointment_date).format('DD MMM hh:mm A')}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 600, color: '#1e293b', border: 'none' }}>
                                                    {parseFloat(appointment.total).toLocaleString()}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 600, color: '#16a34a', border: 'none' }}>
                                                    {parseFloat(appointment.paid).toLocaleString()}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#dc2626', border: 'none' }}>
                                                    {parseFloat(appointment.due).toLocaleString()}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'center', border: 'none' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '4px 12px',
                                                        borderRadius: '20px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        ...(appointment.status === 'partial'
                                                            ? { background: '#fef3c7', color: '#d97706' }
                                                            : { background: '#fee2e2', color: '#dc2626' })
                                                    }}>
                                                        {appointment.status === 'partial' ? t('Partial') : t('Unpaid')}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'center', border: 'none' }}>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => {
                                                            setShowPendingAppointments(false);
                                                            handleMakeCustomerPayment(appointment);
                                                        }}
                                                        disabled={!canManagePayments()}
                                                        style={{
                                                            background: 'linear-gradient(135deg, #0f3460, #1a1a2e)',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            fontSize: '12px',
                                                            fontWeight: 600,
                                                            padding: '6px 14px'
                                                        }}
                                                    >
                                                        <FaMoneyBillWave className="me-1" size={11} />
                                                        {t('Pay Now')}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer style={{ background: '#f8f9fa', borderTop: '1px solid #e2e8f0', padding: '12px 20px' }}>
                    <Button variant="secondary" size="sm" onClick={() => setShowPendingAppointments(false)}>
                        {t('Close')}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
}